import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Grid,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormGroup,
  Checkbox,
  OutlinedInput
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getUrlWithParams } from '../../../utils/Utils';
import ProfileCarousel from './ProfileCarousel';
import { getFunctionsList, getRegionChannelTeamList, saveUserDetails } from './service';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setBusinessUnit } from '../../../redux/HomePage/cyclesByBuSlice';
import { CORE_USER, GENERAL_USER, TPO_ADMIN } from '../../../utils/Constants';
import { setIsExistingUserRx } from '../../../redux/General/generalSlice';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import { fetchListOfActiveBUs } from '../../HomePage/service';

export default function EditProfile({ userDetails }) {
  const [bu, setBU] = React.useState('');
  const [functionOptions, setFunctionOptions] = useState([]);
  const [isBUChangeModalOpen, setIsBUChangeModalOpen] = useState(false);
  const [functionsNewFieldsOptions, setFunctionsNewFieldsOptions] = useState([]);
  const [userFunction, setUserFunction] = useState('');
  const [regionValue, setRegionValue] = useState([]);
  const [channelValue, setChannelValue] = useState([]);
  const [customerValue, setCustomerValue] = useState([]);
  const [showmultiplerows, setShowmultiplerows] = useState(false);
  const otherFunctionCategoryText = '';
  const [defaultBu, setDefaultBu] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const modalText = (
    <>
      We understand that your BU mapping is incorrect.
      <br />
      For Prod, please submit the issue in MyIDM to get it resolved. Once in MyIDM, you’ll need to
      navigate back to the PO Application.
      <br />
      For non-prod, please connect with ldap team.
    </>
  );

  const getFunctionsData = async (bu) => {
    let orderedFunction = [];
    try {
      const data = await getFunctionsList(bu?.[0]);
      if (data) {
        orderedFunction = data.filter((item) => item !== 'Others').concat('Others');
      }
      setFunctionOptions(orderedFunction || []);
    } catch (error) {
      setFunctionOptions([]);
      console.error('Error getting functions list:', error);
    }
  };

  const getFunctionsNewFieldsData = async (bu, param) => {
    setFunctionsNewFieldsOptions([]);
    try {
      const data = await getRegionChannelTeamList(bu, param);
      setFunctionsNewFieldsOptions(data || []);
    } catch (error) {
      console.error('Error getting functions list:', error);
    }
  };

  const handleFuntionChange = (event) => {
    let functionValue = event.target.value;
    setUserFunction(functionValue);

    let param = {
      criteria: functionValue === 'Customer' ? 'Team' : functionValue
    };
    getFunctionsNewFieldsData(userDetails.BUs.toString(), param);
  };

  const handleChangeBU = () => {
    setIsBUChangeModalOpen(true);
  };

  const handleCheckboxChange = (functionName, e) => {
    const value = e.target.value;

    if (functionName === 'Region') {
      setRegionValue((prev) => {
        if (prev.includes(value)) {
          return prev.filter((item) => item !== value);
        } else {
          return [...prev, value];
        }
      });
    } else {
      setChannelValue((prev) => {
        if (prev.includes(value)) {
          return prev.filter((item) => item !== value);
        } else {
          return [...prev, value];
        }
      });
    }
  };

  const handleCustomerValueChange = (e) => {
    setCustomerValue(e.target.value);
  };

  const submitUserDetails = async () => {
    let functionFieldsValue = '';

    if (userFunction === 'Customer') {
      functionFieldsValue = customerValue;
    } else if (userFunction === 'Region') {
      functionFieldsValue = regionValue;
    } else if (userFunction === 'Channel') {
      functionFieldsValue = channelValue;
    }

    if (Array.isArray(functionFieldsValue)) {
      functionFieldsValue = functionFieldsValue?.join(',');
    }

    let params = {
      UserEmail: userDetails.email,
      UserRole: userDetails.roles?.toString(),
      UserBusinessUnits: userDetails.BUs?.toString(),
      UserName: userDetails.username,
      UserId: userDetails.gpid,
      MemberFunction: userFunction === 'Other' ? otherFunctionCategoryText : userFunction,
      FunctionValue: functionFieldsValue
    };

    try {
      const res = await saveUserDetails(params, 'login_page');
      if (res) {
        dispatch(setIsExistingUserRx(true));
        navigate(getUrlWithParams('/home', bu));
      }
    } catch (error) {
      console.error('Error saving details:', error);
    }
  };

  const getListOfActiveBUs = () => {
    fetchListOfActiveBUs().then(async (response) => {
      const activeBus = response?.filter((item) => item.isActive).map((item) => item.bus);
      const filteredList = userDetails.BUs.filter((item) => activeBus.includes(item));
      filteredList.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      if (filteredList && filteredList.length > 0) {
        setDefaultBu(filteredList[0]);
      }
    });
  };

  useMemo(() => {
    getListOfActiveBUs();
  }, []);

  useEffect(() => {
    if (userDetails.BUs && defaultBu) {
      setBU(defaultBu);
      dispatch(setBusinessUnit(defaultBu || ''));
      getFunctionsData(userDetails.BUs);
    }
  }, [userDetails, defaultBu]);

  return (
    <Stack gap="24px" sx={{ padding: '28px', m: '0px 28px' }}>
      <Box>
        <Stack direction="row">
          <Stack direction="column" gap="8px">
            <Typography variant="h4">{`Hi ${userDetails.username}, Welcome to PO!`}</Typography>
            <Typography variant="body1" sx={{ color: '#616161' }}>
              To help us get started, please tell us a little more about yourself.
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          lg={6}
          sx={{ height: '100%', backgroundColor: '#FBFCFE', p: 2, borderRadius: '8px' }}>
          <Stack direction="column" alignItems="center" gap="6px">
            <Box
              gap="6px"
              sx={{
                padding: '8px',
                height: '100%',
                width: '534px',
                boxSizing: 'border-box',
                background: '#FFFFFF',
                border: '1px solid #DFDFDF',
                borderRadius: '8px',
                color: '#616161'
              }}>
              <Stack>
                <Typography fontWeight={400} sx={{ fontSize: 12, color: '#3A3A3A' }}>
                  User Role
                </Typography>
                <Typography sx={{ fontSize: 14 }}>{userDetails.roles}</Typography>
              </Stack>
            </Box>
            <Box
              gap="6px"
              sx={{
                padding: '8px',
                height: '100%',
                width: '534px',
                boxSizing: 'border-box',
                background: '#FFFFFF',
                border: '1px solid #DFDFDF',
                borderRadius: '8px',
                color: '#616161'
              }}>
              <Stack>
                <Typography fontWeight={400} sx={{ fontSize: 12, color: '#3A3A3A' }}>
                  BU
                </Typography>
                <Typography sx={{ fontSize: 14, wordWrap: 'break-word', maxWidth: '100%' }}>
                  {userDetails.BUs && userDetails.BUs.length ? userDetails.BUs.toString() : '-'}
                </Typography>
                {userDetails.roles &&
                  (userDetails.roles.indexOf(CORE_USER) != -1 ||
                    userDetails.roles.indexOf(TPO_ADMIN) != -1) && (
                    <Typography sx={{ fontSize: 14 }}>
                      Mapping not accurate?{' '}
                      <Button
                        variant="text"
                        sx={{
                          '&:hover': {
                            background: 'none'
                          }
                        }}
                        onClick={handleChangeBU}
                        endIcon={<ArrowForwardIcon />}>
                        Change BU{' '}
                      </Button>
                    </Typography>
                  )}
              </Stack>
            </Box>
            {userDetails.roles && userDetails.roles.includes(GENERAL_USER) && (
              <Box
                gap="6px"
                sx={{
                  padding: '8px',
                  height: '100%',
                  width: '567px',
                  position: 'relative',
                  left: '15px',
                  boxSizing: 'border-box',
                  background: '#FFFFFF',
                  border: '1px solid #DFDFDF',
                  borderRadius: '8px',
                  color: '#616161'
                }}>
                <Stack>
                  <Typography fontWeight={400} sx={{ fontSize: 12, color: '#3A3A3A' }}>
                    Select Function
                  </Typography>
                  <FormControl>
                    {/* <FormLabel id="demo-controlled-radio-buttons-group">Gender</FormLabel> */}
                    <RadioGroup
                      row
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      name="controlled-radio-buttons-group"
                      value={userFunction}
                      onChange={handleFuntionChange}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 14
                        },
                        '& .MuiTypography-root': {
                          fontSize: 14
                        }
                      }}>
                      {functionOptions.map((functionName, i) => (
                        <FormControlLabel
                          key={functionName}
                          value={functionName}
                          control={<Radio />}
                          label={functionName}
                        />
                      ))}
                      {userFunction === 'Other' && otherFunctionCategoryText && (
                        <FormControlLabel
                          value={'Other'}
                          control={<Radio />}
                          label={'Other-' + otherFunctionCategoryText}
                        />
                      )}
                    </RadioGroup>
                  </FormControl>
                </Stack>
              </Box>
            )}
            {userDetails.roles &&
              userDetails.roles.indexOf(GENERAL_USER) != -1 &&
              userFunction == 'Customer' && (
                <Box
                  gap="6px"
                  sx={{
                    padding: '8px',
                    height: '100%',
                    width: '534px',
                    boxSizing: 'border-box',
                    background: '#FFFFFF',
                    border: '1px solid #DFDFDF',
                    borderRadius: '8px',
                    color: '#616161'
                  }}>
                  <Stack>
                    <Typography
                      fontWeight={400}
                      sx={{
                        fontSize: 12,
                        color: '#3A3A3A'
                      }}>{`Enter ${userFunction}`}</Typography>
                    <OutlinedInput
                      id="customerValue"
                      value={customerValue}
                      name={'customerValue'}
                      placeholder="Input Customer"
                      onChange={(e) => {
                        handleCustomerValueChange(e);
                      }}
                      onMouseLeave={() => {
                        setShowmultiplerows(false);
                      }}
                      inputProps={{
                        maxLength: 50
                      }}
                      onMouseEnter={() => setShowmultiplerows(true)}
                      multiline={showmultiplerows ? true : false}
                      style={{
                        padding: '5px',
                        width: '100%',
                        height: showmultiplerows ? 'auto' : '32px',
                        fontSize: '14px',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                      }}
                    />
                  </Stack>
                </Box>
              )}
            {userDetails.roles &&
              userDetails.roles.indexOf(GENERAL_USER) != -1 &&
              (userFunction == 'Region' || userFunction == 'Channel') && (
                <Box
                  gap="6px"
                  sx={{
                    padding: '8px',
                    width: '534px',
                    boxSizing: 'border-box',
                    background: '#FFFFFF',
                    border: '1px solid #DFDFDF',
                    borderRadius: '8px',
                    color: '#616161',
                    maxHeight: '6rem',
                    overflowY: 'scroll'
                  }}>
                  <Stack>
                    <Typography
                      fontWeight={400}
                      sx={{
                        fontSize: 12,
                        color: '#3A3A3A'
                      }}>{`Select ${userFunction}s`}</Typography>
                    <FormGroup
                      value={userFunction == 'Region' ? regionValue : channelValue}
                      onChange={(e) => {
                        handleCheckboxChange(userFunction, e);
                      }}
                      row
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 14
                        },
                        '& .MuiTypography-root': {
                          fontSize: 14
                        }
                      }}>
                      {functionsNewFieldsOptions.length
                        ? functionsNewFieldsOptions.map((data, i) => {
                            return (
                              <FormControlLabel
                                key={data}
                                control={<Checkbox value={data} />}
                                label={data}
                              />
                            );
                          })
                        : ''}
                    </FormGroup>
                  </Stack>
                </Box>
              )}
          </Stack>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ width: '534px' }}
              onClick={submitUserDetails}
              disabled={!userDetails?.BUs?.length}>
              Get Started
            </Button>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={3.5}
          md={3.5}
          lg={3.5}
          sx={{
            height: '100%',
            margin: '0px 16px',
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#FBFCFE',
            boxShadow: '4px 0px 24px -2px #005CBC14'
          }}>
          <ProfileCarousel />
        </Grid>
      </Grid>
      {isBUChangeModalOpen && (
        <ConfirmationModal
          open={isBUChangeModalOpen}
          title="Change BU"
          text={modalText}
          SubmitText="Go To MyIDM"
          onClose={() => setIsBUChangeModalOpen(false)}
          onSubmit={() => {
            window.open('https://myidm.mypepsico.com/', '_blank', 'noopener,noreferrer');
          }}
        />
      )}
    </Stack>
  );
}
